import type {
  AgreementDocumentStatus,
  AgreementExecutionStatus,
  AgreementRequiredAction,
  SigningVerificationPolicy,
} from '@/api/schemas/agreements.types';

/**
 * The tones a detail header accepts.
 *
 * Narrower than `BadgeTone`: a status header is a judgement about state, and `info` is not
 * one of the four judgements. Keeping this type here means a status helper cannot return a
 * tone the header will refuse.
 */
export type StatusTone = 'neutral' | 'positive' | 'caution' | 'critical';

/**
 * The words the console uses for the agreement vocabulary.
 *
 * One place, so a status reads the same on every screen. The vocabulary is the API's:
 * `acknowledgment` and `consent` are DISTINCT and are never collapsed into one
 * "acceptance" label — an acknowledgment is a read-and-confirm, a consent is an
 * affirmative permission, and a subject may owe one without the other.
 */

export function requiredActionLabel(action: AgreementRequiredAction): string {
  switch (action) {
    case 'acknowledgment':
      return 'Acknowledgment';
    case 'consent':
      return 'Consent';
    case 'signature':
      return 'Signature';
  }
}

export function documentStatusLabel(status: AgreementDocumentStatus): string {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'published':
      return 'Published';
    case 'withdrawn':
      return 'Withdrawn';
  }
}

export function documentStatusTone(status: AgreementDocumentStatus): StatusTone {
  switch (status) {
    case 'draft':
      return 'neutral';
    case 'published':
      return 'positive';
    case 'withdrawn':
      return 'caution';
  }
}

export function executionStatusLabel(status: AgreementExecutionStatus): string {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'completed':
      return 'Completed';
    case 'declined':
      return 'Declined';
    case 'expired':
      return 'Expired';
  }
}

export function executionStatusTone(status: AgreementExecutionStatus): StatusTone {
  switch (status) {
    case 'pending':
      return 'caution';
    case 'completed':
      return 'positive';
    case 'declined':
      return 'critical';
    case 'expired':
      return 'neutral';
  }
}

/**
 * The assurance a requirement demands, in plain words.
 *
 * `fresh_strong` is a server-recorded WebAuthn step-up. It is NEVER described as a
 * "digital signature" or as legal compliance — it is an identity check.
 */
export function verificationPolicyLabel(policy: SigningVerificationPolicy): string {
  return policy === 'fresh_strong' ? 'Fresh identity check required' : 'Session re-attestation';
}

export function verificationPolicyDescription(policy: SigningVerificationPolicy): string {
  return policy === 'fresh_strong'
    ? 'The signer must complete a fresh identity check (a passkey or security key) immediately before signing.'
    : 'The signer re-proves possession of their signed-in session immediately before signing.';
}

/**
 * Whether a requirement shape is a VENDOR SIGNATURE agreement.
 *
 * For this shape the operator MUST state the verification policy explicitly; the API
 * refuses an absent policy with `INVALID_AGREEMENT_REQUIREMENT` rather than silently
 * defaulting to `session_reattested`.
 */
export function requiresExplicitVerificationPolicy(
  audience: 'tenant' | 'customer',
  requiredAction: AgreementRequiredAction,
): boolean {
  return audience === 'tenant' && requiredAction === 'signature';
}
